import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors, Validators } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  ExternalTransaction,
  TranCategory,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '@helios/common-api'
import { BlankSessionService } from '@helios/sessions'
import {
  AccountInfo,
  RootState,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectFirstFocusedAccountInfo,
  selectFirstLoadedAccountId,
  selectLegacyUserId,
  selectSelectedReconDate,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { compareAsc, isAfter, parseISO } from 'date-fns'
import { Observable, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import {
  BulkTransactionModalInput,
  BulkTransactionModalMode,
} from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BasisDerivedFields,
  BulkDerivedRequestEntry,
  BulkSecurityTransferRow,
  DerivedTpiData,
  securityField,
} from '../../../model/bulk-transaction/bulk-security-transfer/bulk-security-transfer.model'
import {
  BulkSleeveSimpleAccounts,
  SleeveSimple,
} from '../../../model/bulk-transaction/bulk-sleeve-simple-accounts.model'
import {
  AdditionalDetailsSecurityTransfer,
  BulkSecurityTransferTransaction,
  BulkSecurityTransferTransactionDerived,
  ClearwaterTransactionDetailResponse,
  MetaDataTransaction,
  TransactionRequestWrapper,
  TransactionTypeLabel,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { BulkSecurityTransferValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../../../model/counter-party.model'
import { Currency } from '../../../model/currency.model'
import { InventoryType } from '../../../model/inventory-type.model'
import { Basis, MvtBasisSelection } from '../../../model/mvt-basis.model'
import { SecurityMasterData } from '../../../model/security.model'
import { amortOverrideTypesVal } from '../../../model/single-transaction/amort-override-types'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import { MvtData } from '../../../model/single-transaction/market-value-transfer.model'
import {
  TpiPropertiesNumeric,
  TpiPropertiesString,
  TransferPurchaseInfo,
} from '../../../model/single-transaction/security-transfer/transfer-purchase-info.model'
import { Transaction } from '../../../model/single-transaction/transaction'
import { TranCategoryUrl } from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  isBulkCopyModal,
  isBulkCreateModal,
  isBulkEditModal,
  isBulkExternalModal,
} from '../../../util/bulk-popup-input-utils'
import {
  BULK_DIALOG_EVENT_NAME,
  bulkMetricsDetail,
  bulkRequestMode,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  getAccountCellParams,
  numberValueFormatter,
  onSortChange,
} from '../../../util/bulk-specific-common-utils'
import {
  mapBulkExternalTransaction,
  mapLoadToBulkTransaction,
} from '../../../util/bulk-transaction-wrapper-util'
import {
  activateDialog,
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  getTransactionTypeCodeFromKey,
  getTransactionTypeNameFromCode,
  isDialogComponentInActive,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'

@Component({
  selector: 'hs-bulk-security-transfer-in',
  templateUrl: './bulk-security-transfer-in.component.html',
  styleUrls: ['./bulk-security-transfer-in.component.scss'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkSecurityTransferInComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput
  private readonly onPasteEndBinding = this.onPasteEnd.bind(this)
  private readonly adjustTransactionsPostDeleteBinding =
    this.adjustTransactionsPostDelete.bind(this)

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    readonly store: Store<RootState>,
    readonly modalService: NgbModal,
    public bulkTransactionService: CreateEditTransactionService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly blankSessionService: BlankSessionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly telemetryService: TelemetryService,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.store
      .select(selectActualReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.store
      .select(selectSelectedReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((selectedReconDate?: Date) => {
        this.selectedReconDate = selectedReconDate
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }

  private readonly transTypesIn = ['TRNI', 'EXIN']
  missingTranRowList: Set<string> = new Set<string>()
  sortable = false
  rowData: (BulkSecurityTransferRow & unknown)[] = []
  currencyList: Currency[] = []
  private readonly destroySubject$ = new Subject<void>()
  getFieldValidatorFormatter = getFieldValidatorFormatter
  colDefs = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()
  isDataValid = true
  isCommonValidationError = false
  disableSaveValidRows = false
  isValidating = false
  formSubmitting = false
  allDataValidated = false
  lastPastedRowIds: string[] = []
  lastColumnIdsPasted: Set<string> = new Set<string>()

  gridApi?: AgGridApi
  note = new FormControl('')
  reconDate?: Date
  sessionReconDate?: Date
  selectedReconDate?: Date
  sessionId?: string
  private userId?: number
  counterPartyList: CounterParty[] = []
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  genericWarnings: string[] = []
  loadingDialog = false
  title = 'Bulk Security Transfer Create (IN)'
  currentRowIndex = 0
  originalTransactions: BulkSecurityTransferTransaction[] = []
  externalTransaction: ExternalTransaction[] = []
  bulkExternalTransactionDetail: ExternalUnmatchedTransaction[] = []
  isEdit = false
  pastePostProcessingInProgress = false
  deletePostProcessingInProgress = false
  disableValidate = true
  isDeriving = false
  selectedBasisMap: Basis[] = []
  accountList: Set<number> = new Set<number>()
  basisDerivedField?: { [key: string]: BasisDerivedFields }

  securityMasterData: Map<string, SecurityMasterData> = new Map<
    string,
    SecurityMasterData
  >()
  clearwaterTransactionDetail: { [s: string]: Transaction } = {}
  tpiMapByTransaction: {
    [key: string]: { [key: string]: TransferPurchaseInfo }
  } = {}
  mvtMapByTransaction: {
    [key: string]: { [key: string]: MvtData }
  } = {}

  basisMap: { [key: number]: Basis[] } = {}
  isCollateralMap: { [key: number]: boolean } = {}
  basisTypeValues: Basis[] = []
  selectedBasisPrefix = new FormControl([])
  sessionAccountInfo?: AccountInfo
  bulkSleeveSimpleAccount: BulkSleeveSimpleAccounts[] = []
  sleeveSimpleAccount: SleeveSimple[] = []
  sleeveAccountIds: number[] = []
  cusipToSecurityMap = new Map()
  initialBasisIndexMap: Map<string, number> = new Map<string, number>()
  removedBasisIndex: Set<number> = new Set<number>()
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  activeInstances: NgbModalRef[] = []
  compId = 'bulk-security-transfer-in' + Math.random()
  clientLotIdMapBulkPanel: Map<string, string> = new Map<string, string>()
  initialClientLotIdMap: Map<string, string> = new Map<string, string>()
  reprocessTransfers = new FormControl(false)
  showConfirmButtonModal = false
  defaultBasis: Record<string, unknown> = {}
  async ngOnInit() {
    const startTime = Date.now()

    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setNoteErrors()
      })

    this.colDefs = this.createColumnDefs()

    try {
      await this.init()
    } catch (error) {
      this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
      this.errorDetails = error.error
    }
    this.note.markAsTouched()
    this.note.setErrors(this.currentNoteError)

    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.rowData.length,
      this.transInput,
    )
    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Security-Transfer-IN-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  private async init() {
    this.loadingDialog = true
    this.document.addEventListener('pasteDone', this.onPasteEndBinding)
    this.document.addEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )

    if (this.transInput?.extTranId) {
      await this.loadExtTransactionDetailBulk(this.transInput.extTranId)
    }

    if (this.transInput?.bulkStatus) {
      this.transInput.externalTransaction = mapBulkExternalTransaction(
        this.bulkExternalTransactionDetail,
        this.transInput.bulkStatus,
      )
    }

    if (this.transInput?.cwTranId) {
      await this.loadTransactionDetailBulk(
        this.transInput?.cwTranId,
        this.transInput.securityId ?? [],
      )
      this.transInput.transactions = mapLoadToBulkTransaction(
        this.clearwaterTransactionDetail,
      )
    } else if (this.transInput?.transactions) {
      this.originalTransactions = this.transInput
        ?.transactions as BulkSecurityTransferTransaction[]
      await this.loadAdditionalDetailBulk(
        this.originalTransactions.map(
          origTran => origTran.legacyOrigTranId ?? 0,
        ),
        this.originalTransactions.map(origTran => origTran.legacyTranId ?? 0),
        this.originalTransactions.map(origTran => origTran.securityId ?? 0),
        [
          ...new Set(
            this.originalTransactions.map(origTran => origTran.accountId),
          ),
        ],
      )
    }
    if (
      !isBulkCopyModal(this.transInput) &&
      (this.transInput?.transactions || this.transInput?.externalTransaction)
    ) {
      this.missingTranRowList.clear()

      this.disableValidate = false
      this.isEdit = true
      this.pasteService.setIsEdit()

      if (isBulkEditModal(this.transInput)) {
        this.title = 'Bulk Security Transfer Edit (IN)'
        this.originalTransactions = this.transInput
          ?.transactions as BulkSecurityTransferTransaction[]

        await this.loadDataEdit(
          new Set(
            this.originalTransactions.map(origTran => origTran.accountId),
          ),
        )

        this.originalTransactions.forEach((loadTrans, index) => {
          const bulkLoadWithoutTpi: BulkSecurityTransferRow =
            this.generateDataEntryFromTransfer(loadTrans, index)
          const basisWiseTpiDataForTransaction =
            this.getBasisWiseTpiDataForTransaction(
              this.transInput?.cwTranId
                ? loadTrans.transactionId ?? ''
                : loadTrans.legacyTranId?.toString() ?? '',
            )

          const basisWiseMvtDataForTransaction =
            this.getBasisWiseMvtDataForTransaction(
              this.transInput?.cwTranId
                ? loadTrans.transactionId ?? ''
                : loadTrans.legacyOrigTranId?.toString() ?? '',
            )
          this.rowData.push({
            ...bulkLoadWithoutTpi,
            ...basisWiseTpiDataForTransaction,
            ...basisWiseMvtDataForTransaction,
          })
          if (bulkLoadWithoutTpi.transactionId)
            this.initialClientLotIdMap.set(
              bulkLoadWithoutTpi.transactionId,
              bulkLoadWithoutTpi.clientLotId ?? '',
            )
        })
        if (this.gridApi) {
          await this.savedViewService.manageSavedView(
            this.gridApi,
            'Bulk-security-transfer-in-workspace-key-mode-edit',
            this.destroySubject$,
          )
          this.gridApi.api.setSortModel(null)
        }
      } else if (isBulkExternalModal(this.transInput)) {
        this.title = 'Bulk Security Transfer Post (IN)'
        this.externalTransaction = this.transInput
          ?.externalTransaction as ExternalTransaction[]

        await this.loadDataEdit(
          new Set(this.externalTransaction.map(origTran => origTran.accountId)),
        )
        if (!this.transInput?.extTranId) {
          const uniqueAccountIdExternal = [
            ...new Set(this.externalTransaction.map(obj => obj.accountId)),
          ]
          await this.loadNonSessionAccountTypeInfo(uniqueAccountIdExternal)
        }
        this.externalTransaction.forEach((extTrans, index) => {
          this.rowData.push(
            this.generateDataEntryFromExternalTransfer(extTrans, index),
          )
        })
        if (this.gridApi) {
          await this.savedViewService.manageSavedView(
            this.gridApi,
            'Bulk-security-transfer-in-workspace-key-mode-external',
            this.destroySubject$,
          )
          this.gridApi.api.setSortModel(null)
        }
      }
    } else if (isBulkCopyModal(this.transInput)) {
      this.disableValidate = false
      this.originalTransactions = this.transInput
        ?.transactions as BulkSecurityTransferTransaction[]

      await this.loadDataEdit(
        new Set(this.originalTransactions.map(origTran => origTran.accountId)),
      )

      this.originalTransactions.forEach((loadTrans, index) => {
        const bulkLoadWithoutTpi: BulkSecurityTransferRow =
          this.generateDataEntryFromTransfer(loadTrans, index)
        const basisWiseTpiDataForTransaction =
          this.getBasisWiseTpiDataForTransaction(
            this.transInput?.cwTranId
              ? loadTrans.transactionId ?? ''
              : loadTrans.legacyTranId?.toString() ?? '',
          )
        const basisWiseMvtDataForTransaction =
          this.getBasisWiseMvtDataForTransaction(
            this.transInput?.cwTranId
              ? loadTrans.transactionId ?? ''
              : loadTrans.legacyOrigTranId?.toString() ?? '',
          )
        this.rowData.push({
          ...bulkLoadWithoutTpi,
          ...basisWiseTpiDataForTransaction,
          ...basisWiseMvtDataForTransaction,
        })
      })
      this.rowData.push(
        this.generateEmptyDataEntry(false, this.rowData.length + 1),
      )
    } else {
      this.fetchAllBasis()
      this.createFreshRowData()
    }

    this.gridApi?.api.applyTransaction({ add: this.rowData })

    this.setSleeveSimpleAccountIdsPerRow()

    this.loadingDialog = false
  }

  fetchAllBasis() {
    this.bulkTransactionService
      .fetchAllBasis()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        allbasis => {
          this.basisTypeValues = allbasis.sort((a: Basis, b: Basis) =>
            a.prefix.localeCompare(b.prefix),
          )
          this.selectAllForDropdownItems(this.basisTypeValues)
        },
        error => {
          this.errorHeader = 'Error Fetching Basis'
          this.errorDetails = error.error
        },
      )
  }

  setSleeveSimpleAccountIdsPerRow() {
    if (this.bulkSleeveSimpleAccount.length > 0)
      this.rowData.forEach(node => {
        node.sleeveSimpleAccountIds = this.bulkSleeveSimpleAccount
          .filter(
            sleeveSimpleId =>
              sleeveSimpleId.id === Number(node.externalAccountId),
          )
          .map(data => data.sleeveSimple)[0]
      })
    else
      this.rowData.forEach(
        node => (node.sleeveSimpleAccountIds = this.sleeveSimpleAccount),
      )
  }

  async loadExtTransactionDetailBulk(transInput: number[]) {
    const sessionAccountId = await this.store
      .select(selectFirstLoadedAccountId)
      .pipe(take(1))
      .toPromise()
    const transactionDetail$: Observable<ExternalUnmatchedTransaction[]> =
      this.bulkTransactionService.loadExtTransactionDetailBulk(
        transInput,
        sessionAccountId,
      )
    const accountInfo$ = this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(take(1))
    return Promise.all([
      transactionDetail$.toPromise(),
      accountInfo$.toPromise(),
    ]).then(async ([extTransactionDetail, accountInfo]) => {
      if (this.transInput) {
        // Create a Map with the transaction details keyed by their id for faster lookup
        const transactionDetailById = new Map(
          extTransactionDetail.map(td => [td.externalTransactionId, td]),
        )
        // Sort the transaction details based on the order of transInput
        this.bulkExternalTransactionDetail = transInput
          .map(id => transactionDetailById.get(id))
          .filter((td): td is ExternalUnmatchedTransaction => Boolean(td))
        await this.setSessionAccounInfo(accountInfo)
      }
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getBasisWiseTpiDataForTransaction(tranId: string): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tranRowWithBasis: any = {}
    const basisWiseTpi = this.tpiMapByTransaction[tranId]
    if (basisWiseTpi) {
      const basisWiseTpiMap = new Map(Object.entries(basisWiseTpi))
      basisWiseTpiMap.forEach((value: TransferPurchaseInfo, key: string) => {
        const basisPrefix =
          this.basisTypeValues.find(basis => basis.basisId == Number(key))
            ?.prefix ?? ''
        let property: keyof typeof value
        for (property in value) {
          tranRowWithBasis[
            this.concatToCamelCase(
              property == 'taxDaysHeld' ? '' : basisPrefix,
              property,
            )
          ] = value[property]
        }
      })
    }
    return tranRowWithBasis
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getBasisWiseMvtDataForTransaction(tranId: string): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mvtRowWithBasis: any = {}
    const basisWiseMvt = this.mvtMapByTransaction[tranId]
    if (basisWiseMvt) {
      const basisWiseMvtMap = new Map(Object.entries(basisWiseMvt))
      basisWiseMvtMap.forEach((value: MvtData, key: string) => {
        const basis = this.basisTypeValues.find(b => b.basisId == Number(key))
        if (basis) mvtRowWithBasis[basis.basisId] = basis
      })
    }
    return this.createMvtBases(mvtRowWithBasis)
  }

  createMvtBases(mvtBases: { [k: string]: Basis }): Record<string, unknown> {
    return {
      mvtBases,
    }
  }

  generateDataEntryFromTransfer(
    cwTransaction: BulkSecurityTransferTransaction,
    index: number,
  ): BulkSecurityTransferRow {
    const factor = cwTransaction.factor ?? 1.0
    const transferPrice = cwTransaction.transferPrice
    const securityDetails = this.securityMasterData.get(
      cwTransaction.securityId + '',
    )
    const clientLotId =
      this.clientLotIdMapBulkPanel.get(cwTransaction.legacyOrigTranId + '') ??
      ''
    const securityTransferIN = {
      legacyOrigTranId: cwTransaction.legacyOrigTranId ?? -1,
      transactionId: cwTransaction.transactionId ?? '',
      legacyTranId: cwTransaction.legacyTranId ?? -1,
      rowId: (index + 1).toString(),
      accountId: cwTransaction.accountId.toString(),
      tranType: getTransactionTypeCodeFromKey(cwTransaction.tranType),
      units: cwTransaction.units?.toString() ?? '',
      entryDate: cwTransaction.entryDate,
      assetId: securityDetails?.cusip ?? '',
      securityId: cwTransaction.securityId,
      factor: factor,
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      transferPrice: transferPrice?.toString() ?? '',

      inventoryType: cwTransaction.inventoryType
        ? InventoryType[
            cwTransaction.inventoryType as keyof typeof InventoryType
          ]
        : InventoryType.LONG,
      collateralBroker: cwTransaction.dataMap?.collateralBroker ?? '',
      clientLotId: cwTransaction.clientLotId ?? clientLotId,
      transactionBasis: cwTransaction.transactionBasis,
      basisId: cwTransaction.basisId,
    } as BulkSecurityTransferRow
    if (cwTransaction.orderId) {
      securityTransferIN.orderId = cwTransaction.orderId
    }
    return securityTransferIN
  }

  generateDataEntryFromExternalTransfer(
    extTransaction: ExternalTransaction,
    index: number,
  ): BulkSecurityTransferRow {
    const factor = extTransaction.factor ?? 1.0
    const transferPrice = extTransaction.price
    let accountId = extTransaction.accountId.toString()
    if ('CUSTODY' === extTransaction.dataSourceType) {
      if (this.hideAccountForSleeveAggregate(extTransaction.accountId)) {
        accountId = ''
      }
    }
    const externalTransferIn = {
      rowId: (index + 1).toString(),
      accountId: accountId,
      tranType: extTransaction.tranTypeCode
        ? getTransactionTypeCodeFromKey(
            extTransaction.tranTypeCode as TransactionTypeKey,
          )
        : '',
      units: extTransaction.units?.toString() ?? '',
      entryDate: extTransaction.tradeDate ?? '',
      assetId: extTransaction?.secIdentifier ?? '',
      securityId: extTransaction?.securityId,
      factor: factor,
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      transferPrice: transferPrice?.toString() ?? '',
      prepaidInterest: extTransaction.prepaidInterest,
      interestDue: extTransaction.interestDue,

      inventoryType: extTransaction.inventoryType
        ? InventoryType[
            extTransaction.inventoryType as keyof typeof InventoryType
          ]
        : InventoryType.LONG,
      collateralBroker:
        extTransaction.collateralBroker ??
        extTransaction.prometheusJson.aggInfo?.collateralBroker ??
        '',
      executionId: extTransaction.executionId,
      externalTranId: extTransaction.extTranId,
      externalAccountId: extTransaction.accountId,
      externalCusip: extTransaction.secIdentifier,
      externalTranStatus: extTransaction.status,
      externalDataSourceType: extTransaction.dataSourceType,
      transactionTypeLabel: extTransaction.transactionTypeLabel,
    } as BulkSecurityTransferRow

    if (!extTransaction.tranTypeCode) {
      const tranTypeMap = new Map<string, string[]>()
      tranTypeMap.set('tranType', ['Valid Value Required'])
      this.missingTranRowList.add((index + 1).toString())
      this.disableValidate = true
      externalTransferIn.validityStatus = 'invalid'
      externalTransferIn.errorMessageMap = tranTypeMap
    }
    return externalTransferIn
  }

  createFreshRowData() {
    this.rowData = []
    this.rowData.push(this.generateEmptyDataEntry(true, 1))
    for (let i = 1; i <= 9; i++) {
      this.rowData.push(this.generateEmptyDataEntry(false, i + 1))
    }
  }

  generateEmptyDataEntry(
    activated: boolean,
    id: number,
  ): BulkSecurityTransferRow {
    const tranTypeMap = new Map<string, string[]>()
    tranTypeMap.set('tranType', ['Valid Value Required'])
    if (activated) {
      this.missingTranRowList.add(id.toString())
    }

    return {
      rowId: id.toString(),
      accountId: '',
      tranType: '',
      units: '',
      entryDate: '',
      assetId: '',
      validityStatus: activated ? 'invalid' : 'inactive',
      errorMessageMap: activated ? tranTypeMap : new Map(),
      transferPrice: '',
      inventoryType: '',
      collateralBroker: '',
      prepaidInterest: '',
      interestDue: '',
    }
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener('pasteDone', this.onPasteEndBinding)
    this.document.removeEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )
  }

  /** @internal */
  async loadDataEdit(accounts: Set<number>) {
    const basisObservable = await this.bulkTransactionService
      .loadBasisForAccounts(Array.from(accounts))
      .toPromise()
    const collateralObservable = await this.bulkTransactionService
      .isCollateralBroker(Array.from(accounts))
      .toPromise()
    return Promise.all([basisObservable, collateralObservable]).then(
      ([basisMap, isCollateralMap]) => {
        this.basisMap = basisMap
        this.isCollateralMap = isCollateralMap
        this.setUpBasis()
      },
    )
  }

  setUpBasis() {
    // flatten the arrays of basis objects into a single array and remove duplicates
    this.basisTypeValues = JSON.parse(
      JSON.stringify(
        Object.values(this.basisMap)
          .reduce(
            (accumulator, currentValue) => [...accumulator, ...currentValue],
            [],
          )
          .reduce((accumulator: Basis[], currentValue: Basis) => {
            if (
              !accumulator.some(
                accountBasis => accountBasis.basisId === currentValue.basisId,
              )
            ) {
              accumulator.push(currentValue)
            }
            return accumulator
          }, []),
      ),
    )
    this.selectAllForDropdownItems(this.basisTypeValues)
    this.selectedBasisPrefix.patchValue(
      this.basisTypeValues.map(basis => basis.prefix),
    )
    this.basisTypeValues.forEach((accountBasis, index) => {
      const applicableAccounts = this.findKeysWithBasisPrefix(
        accountBasis.prefix,
      )
      this.initialBasisIndexMap.set(accountBasis.prefix, index)
      this.colDefs = this.colDefs.concat(
        this.addBasisColumn(
          accountBasis.prefix,
          applicableAccounts.join().split(','),
        ),
      )
    })

    this.gridApi?.api.setColumnDefs(this.colDefs)
  }

  addBasisColumn(
    basisPrefix: string,
    applicableAccounts: string[],
  ): ColGroupDef {
    return {
      headerName: `${basisPrefix} Details`,
      groupId: basisPrefix,
      openByDefault: true,
      children: [
        {
          field: 'collapsedMarker',
          headerName: 'Expand to Edit Basis Details...',
          width: 200,
          columnGroupShow: 'closed',
        },
        {
          field: `${basisPrefix}OrigTradeDate`,
          headerName: `${basisPrefix} Orig Trade Date`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Date,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigSettleDate`,
          headerName: `${basisPrefix} Orig Settle Date`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Date,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigCost`,
          headerName: `${basisPrefix} Orig Cost`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigPrice`,
          headerName: `${basisPrefix} Orig Price`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigAccruedInt`,
          headerName: `${basisPrefix} Accrued`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}Commission`,
          headerName: `${basisPrefix} Commission`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortStartCost`,
          headerName: `${basisPrefix} Amort Start Cost`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortStartPrice`,
          headerName: `${basisPrefix} Amort Start Price`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,

          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}Factor`,
          headerName: `${basisPrefix} Factor`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,

          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortStartDate`,
          headerName: `${basisPrefix} Amort Start Date`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Date,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortOverride`,
          headerName: `${basisPrefix} Amort Override`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Select,
            options: {
              items: amortOverrideTypesVal?.map(a => a.name),
            },
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigYield`,
          headerName: `${basisPrefix} Yield`,
          type: getInlineEditColumnType(),
          editable: false,
          columnGroupShow: 'open',

          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          comparator: customSortComparatorBulkNumeric,

          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AccumulatedOtti`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} Default Accumulated OTTI`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,

          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}DaysImpaired`,
          headerName: `${basisPrefix} Days Impaired`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TaxDaysHeld',
          headerName: 'Tax Days Held',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXAccruedMarketDiscount',
          headerName: 'Tax Accrued Market Discount',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXWashSaleDeferredLoss',
          headerName: 'Tax Wash Sale Deferred Loss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxOrigCost`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Original Cost`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxBookValueAtLoad`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Book Value at Load`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxAccruedAtLoad`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Accrued at Load`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}LocalAccruedAtLoad`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} Accrued at Load`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxAccumulatedOtti`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Default Accumulated OTTI`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}DerivedAmortStartPrice`,
          hide: true,
        },
        {
          field: `${basisPrefix}DerivedAmortStartCost`,
          hide: true,
        },
        {
          field: `${basisPrefix}DerivedFactor`,
          hide: true,
        },
        {
          field: 'TAXFxAccruedMarketDiscount',
          type: getInlineEditColumnType(),
          headerName: 'TAX FX Accrued Market Discount',
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxWashSaleDeferredLoss',
          type: getInlineEditColumnType(),
          headerName: `TAX FX Wash Sale Deferred Loss`,
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXStraddleDeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxStraddleDeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXSection1256DeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxSection1256DeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}LpCumulativeEarnings`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} LP Cumulative Earnings`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}LpCumulativeDistributions`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} LP Cumulative Distributions`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}ReturnOfInvestment`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} Return of Investment`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}ReturnOnInvestment`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} Return on Investment`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId) ||
            !isBulkEditModal(this.transInput),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
      ],
    }
  }

  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event
    this.pasteService.registerGrid(event.api, event.columnApi)
    this.copyService.registerGrid(event.api)
    this.bulkDeleteService.registerGrid(event.api)
    this.gridApi?.api.setColumnDefs(this.colDefs)
    if (
      isBulkCreateModal(this.transInput) ||
      isBulkCopyModal(this.transInput)
    ) {
      await this.savedViewService.manageSavedView(
        event,
        'Bulk-security-transfer-in-workspace-key-mode-create',
        this.destroySubject$,
      )
    }
    this.gridApi.api.setSortModel(null)
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: async event => this.onGridReady(event),
      getRowNodeId: ({ rowId }: BulkSecurityTransferRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },

      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.colDefs,
      tooltipShowDelay: 0,
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
    }
  }

  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  async onPasteEnd(evt: Event) {
    const pasteDetails = (evt as CustomEvent).detail
    this.pastePostProcessingInProgress = true
    this.disableValidate = true
    this.loadingDialog = true
    this.lastPastedRowIds = pasteDetails.pastedRowData.map(
      (rowData: { rowId: BulkSecurityTransferRow }) => rowData.rowId,
    )
    this.lastColumnIdsPasted = pasteDetails.colPastedSet

    this.pastePostProcessingInProgress = false
    this.applyOnChangeForPastedRowNodes()
    if (this.missingTranRowList.size == 0) this.disableValidate = false

    this.loadingDialog = false
  }

  applyOnChangeForPastedRowNodes() {
    this.lastPastedRowIds.forEach(rowId => {
      const displayedNode = this.gridApi?.api.getRowNode(rowId)
      if (displayedNode) {
        this.applyOnChangeForPastedRowNode(displayedNode)
      }
    })
  }

  applyOnChangeForPastedRowNode(displayedNode: RowNode) {
    const allColumns = this.gridApi?.columnApi.getAllColumns()
    allColumns
      ?.filter(column =>
        this.lastColumnIdsPasted.has(column.getColDef().field ?? ''),
      )
      .forEach(currentColumn => {
        const colId = currentColumn.getColDef().field
        this.onColumnValueChanged({
          column: { getColId: () => colId },
          node: displayedNode,
          data: displayedNode.data,
          newValue: displayedNode?.data[colId ?? ''],
          oldValue: '',
        } as unknown as CellValueChangedEvent)
      })
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    const rowNode = event.node
    const colId = event.column.getColId()
    if (
      rowNode.data[colId] === event.oldValue ||
      this.deletePostProcessingInProgress ||
      this.pastePostProcessingInProgress
    ) {
      return
    }
    if (
      rowNode?.data.validityStatus == 'inactive' &&
      (this.transInput?.modalMode == BulkTransactionModalMode.CreateCWTran ||
        this.transInput?.modalMode == BulkTransactionModalMode.CopyCWTran) &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap'
    ) {
      if (colId != 'tranType') {
        const tranTypeMap = new Map<string, string[]>()
        tranTypeMap.set('tranType', ['Valid Value Required'])
        rowNode.data.validityStatus = 'invalid'
        rowNode.data.errorMessageMap = tranTypeMap
        this.disableValidate = true
      }

      if (
        Number(rowNode?.rowIndex) + 1 ==
        this.gridApi?.api.getDisplayedRowCount()
      ) {
        const rows = [
          this.generateEmptyDataEntry(false, Number(rowNode.data.rowId) + 1),
        ]
        this.gridApi?.api.applyTransaction({ add: rows })
      }
    }
    if (
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.data.validityStatus = 'intermediate'
      this.allDataValidated = false
    }
    if (colId == 'tranType') {
      this.missingTranRowList.delete(event.node.data.rowId)
      if (this.missingTranRowList.size == 0) this.disableValidate = false
    } else if (colId == 'validityStatus') {
      this.onValidityStatusChanged(event)
    } else if (colId == 'accountId') {
      if (this.bulkSleeveSimpleAccount.length > 0) {
        this.sleeveSimpleAccount = this.bulkSleeveSimpleAccount
          .filter(data => data.id === Number(event.data['externalAccountId']))
          .map(data => data.sleeveSimple)[0]
      }
      if (this.sleeveSimpleAccount && this.sleeveSimpleAccount.length > 0) {
        const sAccount = this.sleeveSimpleAccount?.filter(
          data => data.displayLabel == event.data['accountId'],
        )[0]?.id
        if (sAccount) {
          event.data['sleeveAccountId'] = sAccount
        } else {
          rowNode.data.accountId = ''
        }
      }
    }
  }

  showIDCols() {
    const legacyOrigTranIdColumn: ColDef = {
      field: securityField('legacyOrigTranId'),
      headerName: 'Orig Transaction ID',
      columnGroupShow: 'open',
      comparator: customSortComparatorBulkNumeric,
    }

    const externalTranIdColumn: ColDef = {
      field: securityField('externalTranId'),
      headerName: 'Ext Transaction ID',
      columnGroupShow: 'open',
      comparator: customSortComparatorBulkNumeric,
    }

    const orderIdColumn: ColDef = {
      field: securityField('orderId'),
      headerName: 'Order ID',
      comparator: customSortComparatorBulkNumeric,
    }
    if (isBulkExternalModal(this.transInput)) return [externalTranIdColumn]
    else if (
      this.transInput?.transactions?.[0].orderId &&
      isBulkEditModal(this.transInput)
    )
      return [legacyOrigTranIdColumn, orderIdColumn]
    else if (isBulkEditModal(this.transInput)) return [legacyOrigTranIdColumn]
    else return []
  }

  private createColumnDefs(): (ColDef | ColGroupDef)[] {
    const colDefs: (ColDef | ColGroupDef)[] = [
      {
        field: securityField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: (params: { data: { validityStatus: string } }) =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: securityField('rowNumber'),
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
        sortable: false,
      },
      {
        field: securityField('errorMessageMap'),
        hide: true,
      },
      {
        headerName: 'Transaction Details',
        groupId: 'TransactionDetails',
        openByDefault: true,
        children: [
          ...this.showIDCols(),
          {
            field: securityField('accountId'),
            headerName: 'Account ID',
            width: 100,
            type: getInlineEditColumnType(),
            cellEditorSelector: (x: EditableCallbackParams) =>
              getAccountCellParams(
                x,
                !!this.transInput?.externalTransaction,
                this.sessionAccountInfo?.accountType,
              ),
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            editable: (x: EditableCallbackParams) => this.isAccountEditable(x),
            valueFormatter: (params: ValueFormatterParams) =>
              params.value ?? '',
          },
          {
            field: securityField('tranType'),
            headerName: 'Tran Type',
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,

            width: 100,
            cellStyle: (params: CellClassParams) => {
              return this.deduceTranTypeCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.deduceTranTypeToolTip(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            cellEditorParams: {
              validators: [Validators.required],
              dataType: InlineEditDataType.Select,
              options: {
                items: this.transTypesIn,
              },
            },
            editable: () => !isBulkEditModal(this.transInput),
          },
          {
            field: securityField('assetId'),
            headerName: 'Asset ID',
            width: 120,
            type: getInlineEditColumnType(),
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            cellEditorParams: {
              dataType: InlineEditDataType.String,
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            editable: () => !isBulkEditModal(this.transInput),
            valueFormatter: (params: ValueFormatterParams) =>
              params.value ?? '',
          },

          {
            field: securityField('entryDate'),
            headerName: 'Transfer Date',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Date,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: securityField('units'),
            headerName: 'Orig Units',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            comparator: customSortComparatorBulkNumeric,
            valueFormatter: (params: ValueFormatterParams) =>
              params.value ?? '',
          },

          {
            field: securityField('transferPrice'),
            headerName: 'Transfer Price',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
              disablePopover: true,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            comparator: customSortComparatorBulkNumeric,
            valueFormatter: (params: ValueFormatterParams) =>
              params.value ?? '',
          },

          {
            field: securityField('inventoryType'),
            headerName: 'Inventory Type',
            width: 120,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Select,
              options: {
                items: Object.values(InventoryType),
              },
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            valueFormatter: (params: ValueFormatterParams) =>
              params.value ?? '',
          },

          {
            field: securityField('collateralBroker'),
            headerName: 'Collateral Broker',
            width: 100,
            type: getInlineEditColumnType(),
            cellEditorParams: {
              dataType: InlineEditDataType.String,
              disablePopover: true,
            },
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            editable: (params: EditableCallbackParams) =>
              this.isCollateralEditable(params),
          },

          {
            field: securityField('prepaidInterest'),
            headerName: 'Loan Prepaid Interest',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            valueFormatter: (params: ValueFormatterParams) =>
              params.value !== ''
                ? numberValueFormatter(Number(params.value), 2, 2)
                : '',
          },
          {
            field: securityField('interestDue'),
            headerName: 'Loan Past Due Interest',
            width: 90,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            valueFormatter: (params: ValueFormatterParams) =>
              params.value !== ''
                ? numberValueFormatter(Number(params.value), 2, 2)
                : '',
          },
          {
            field: securityField('transactionBasis'),
            hide: true,
          },
          {
            field: securityField('transactionTypeLabel'),
            hide: true,
          },
          {
            field: securityField('basisId'),
            hide: true,
          },
        ],
      },
    ]
    return colDefs
  }

  isCollateralEditable(params: EditableCallbackParams) {
    if (
      this.isCollateralMap?.[params.data.accountId] ||
      !isBulkEditModal(this.transInput)
    ) {
      return true
    }
    return false
  }

  disableSaveValidRowsButton() {
    let disable = true

    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })

    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      'accountId',
      this.deduceImpactedField('accountId'),
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async deriveAndValidate() {
    if (this.markMissingAccountRows()) {
      return
    }
    this.errorDetails = ''
    await this.populateDerivedFields()
    await this.validate()
  }

  async populateDerivedFields() {
    this.isDeriving = true
    this.loadingText = TransactionConstants.FETCH_DERIVED_FIELDS
    const tranData = this.getGridTransactionsAndTpiForDerived()
    if (!this.isEdit) {
      const basisObject: { [key: number]: Basis[] } = {}
      this.accountList.forEach(acc => {
        basisObject[acc] = this.selectedBasisMap
      })
      this.basisMap = basisObject
    }
    try {
      await this.loadMissingAccountBases()
      const derivedResponse = await this.bulkTransactionService
        .getDerivedFieldsForSecurityTransfer({
          tranData: tranData,
          accountBases: this.basisMap,
          requestMode: bulkRequestMode(this.transInput?.modalMode),
        })
        .toPromise()
      if (derivedResponse.securityIdMap) {
        this.cusipToSecurityMap = new Map(
          Object.entries(derivedResponse.securityIdMap),
        )
      }
      this.basisDerivedField = derivedResponse.basisDerivedField
      // PVAL-26104 (BC-11 / BC-11b): reset-then-apply for the derive-only amount
      // slot. The loop below iterates the *response*, so a row the backend omits
      // is never visited. Clearing `derivedAmount` on every grid row first means
      // the slot can only ever hold a value the current derive actually returned,
      // so a stale proceeds amount can never survive into the save command. The
      // grid nodes are the same collection the save path walks.
      this.gridApi?.api?.forEachNode(rowNode => {
        if (rowNode.data) {
          rowNode.data.derivedAmount = undefined
        }
      })
      new Map(Object.entries(derivedResponse.basisDerivedField)).forEach(
        (value, key) => {
          const currentRow = this.gridApi?.api.getRowNode(key ?? '')?.data
          if (currentRow) {
            if (value.transactionDerivedFields.transferPrice != undefined) {
              currentRow.transferPrice =
                value.transactionDerivedFields.transferPrice
            }
            // PVAL-26104: capture the server-derived proceeds into the derive-only
            // slot, kept separate from the row's own `amount` so the two sources
            // can no longer overwrite one another. Assigned unconditionally (no
            // `!= undefined` guard) so that a legitimate 0 is kept and an omitted
            // field clears the slot instead of leaving the previous cycle's value.
            currentRow.derivedAmount = value.transactionDerivedFields.amount

            const tpiMap: Map<string, DerivedTpiData> = new Map(
              Object.entries(value.tpiDerivedFields),
            )
            this.defaultBasis = {}
            tpiMap.forEach((tpiDerivedFields, tpiBasisId) => {
              const basisPrefix = this.basisTypeValues.find(
                basis => basis.basisId == Number(tpiBasisId),
              )?.prefix

              if (tpiDerivedFields.amortStartPrice != undefined) {
                ;(currentRow[
                  `${basisPrefix}DerivedAmortStartPrice` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.amortStartPrice
              } else {
                currentRow[
                  `${basisPrefix}DerivedAmortStartPrice` as keyof typeof currentRow
                ] = undefined
              }
              if (tpiDerivedFields.amortStartCost != undefined) {
                ;(currentRow[
                  `${basisPrefix}DerivedAmortStartCost` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.amortStartCost
              } else {
                currentRow[
                  `${basisPrefix}DerivedAmortStartCost` as keyof typeof currentRow
                ] = undefined
              }
              if (tpiDerivedFields.factor != undefined) {
                ;(currentRow[
                  `${basisPrefix}DerivedFactor` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.factor
              } else {
                currentRow[
                  `${basisPrefix}DerivedFactor` as keyof typeof currentRow
                ] = undefined
              }
              if (
                tpiDerivedFields.factor != undefined &&
                (!currentRow[
                  `${basisPrefix}Factor` as keyof typeof currentRow
                ] as unknown as number)
              ) {
                ;(currentRow[
                  `${basisPrefix}Factor` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.factor
              }

              if (
                tpiDerivedFields.amortStartPrice != undefined &&
                (!currentRow[
                  `${basisPrefix}AmortStartPrice` as keyof typeof currentRow
                ] as unknown as number)
              ) {
                ;(currentRow[
                  `${basisPrefix}AmortStartPrice` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.amortStartPrice
              }

              if (
                tpiDerivedFields.amortStartCost != undefined &&
                (!currentRow[
                  `${basisPrefix}AmortStartCost` as keyof typeof currentRow
                ] as unknown as number)
              ) {
                ;(currentRow[
                  `${basisPrefix}AmortStartCost` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.amortStartCost
              }

              if (
                tpiDerivedFields.origCost &&
                (!currentRow[
                  `${basisPrefix}OrigCost` as keyof typeof currentRow
                ] as unknown as number)
              ) {
                ;(currentRow[
                  `${basisPrefix}OrigCost` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.origCost
              }

              if (
                tpiDerivedFields.origPrice &&
                (!currentRow[
                  `${basisPrefix}OrigPrice` as keyof typeof currentRow
                ] as unknown as number)
              ) {
                ;(currentRow[
                  `${basisPrefix}OrigPrice` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.origPrice
              }

              if (tpiDerivedFields.origPrice != undefined) {
                ;(currentRow[
                  `${basisPrefix}OrigPrice` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.origPrice
              }
              if (tpiDerivedFields.origYield != undefined) {
                ;(currentRow[
                  `${basisPrefix}OrigYield` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.origYield ?? 0
              }
              if (tpiDerivedFields.factor != undefined) {
                ;(currentRow[
                  `factor` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.factor ?? 0
              }
              if (tpiDerivedFields.origAccruedInt != undefined) {
                ;(currentRow[
                  `${basisPrefix}OrigAccruedInt` as keyof typeof currentRow
                ] as unknown as number) = tpiDerivedFields.origAccruedInt ?? 0
              }
              if (this.isEdit) {
                // Fill mandatory fields
                this.fillMandatoryFieldsFromGAAP(currentRow, basisPrefix)

                if (basisPrefix === 'GAAP') {
                  this.defaultBasis = Object.keys(currentRow)
                    .filter(gaapKey => gaapKey.startsWith('GAAP'))
                    .reduce<Record<string, unknown>>((gaapObj, gaapKey) => {
                      gaapObj[gaapKey] =
                        currentRow[gaapKey as keyof typeof currentRow]
                      return gaapObj
                    }, {})
                }
              }
            })
          }
        },
      )
      this.isDeriving = false
    } catch (error) {
      this.errorHeader = TransactionConstants.ERROR_FETCH_DERIVED_FIELDS
      this.errorDetails = error.error
      this.isDeriving = false
    }
  }

  fillMandatoryFieldsFromGAAP(
    currentRow: Record<string, unknown>,
    basisPrefix?: string,
  ) {
    if (
      basisPrefix === 'GAAP' ||
      !this.defaultBasis ||
      Object.keys(this.defaultBasis).length === 0
    ) {
      return
    }

    const mandatoryFields = [
      'OrigPrice',
      'Commission',
      'AmortStartPrice',
      'OrigAccruedInt',
      'AmortStartDate',
      'OrigSettleDate',
      'OrigTradeDate',
    ]

    mandatoryFields.forEach(field => {
      const currentFieldKey = `${basisPrefix}${field}`
      const gaapFieldKey = `GAAP${field}`

      // Check if current field is empty/undefined and GAAP value exists
      if (
        (currentRow[currentFieldKey] === undefined ||
          currentRow[currentFieldKey] === null ||
          currentRow[currentFieldKey] === '') &&
        this.defaultBasis[gaapFieldKey] !== undefined &&
        this.defaultBasis[gaapFieldKey] !== null &&
        this.defaultBasis[gaapFieldKey] !== ''
      ) {
        currentRow[currentFieldKey] = this.defaultBasis[gaapFieldKey]
      }
    })
  }

  // On a sleeve aggregate the Account cell holds the sleeve display label; the id is sleeveAccountId.
  resolveRowAccountId(rowData: BulkSecurityTransferRow): number {
    return Number(rowData.sleeveAccountId ?? rowData.accountId)
  }

  // Edit/post modes load bases only for the source accounts; the server needs one per derived row's account.
  async loadMissingAccountBases() {
    const missing = [...this.accountList].filter(
      acc => acc > 0 && !this.basisMap[acc],
    )
    if (missing.length === 0) return
    const loaded = await this.bulkTransactionService
      .loadBasisForAccounts(missing)
      .toPromise()
    this.basisMap = { ...this.basisMap, ...loaded }
  }

  getGridTransactionsAndTpiForDerived(): BulkDerivedRequestEntry[] {
    const transactionsAndTpi: BulkDerivedRequestEntry[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactionsAndTpi.push(
          this.prepareBulkDerivedRequestData(rowNode.data),
        )
    })
    return transactionsAndTpi
  }

  prepareBulkDerivedRequestData(
    rowData: BulkSecurityTransferRow,
  ): BulkDerivedRequestEntry {
    const originalSettleDate = rowData[
      'GAAPOrigSettleDate' as keyof typeof rowData
    ] as string

    const accountId = this.resolveRowAccountId(rowData)
    this.accountList.add(accountId)

    const transTypeCode = Object.keys(TransactionTypeEnum).filter(
      x =>
        TransactionTypeEnum[x as TransactionTypeKey] ==
        getTransactionTypeNameFromCode(rowData.tranType),
    )[0] as TransactionTypeKey

    const dataMap: { [key: string]: string | number | undefined } = {
      collateralBroker: rowData.collateralBroker,
    }

    if (rowData.prepaidInterest) {
      dataMap['prepaidInterest'] = Number(rowData.prepaidInterest)
    }

    if (rowData.interestDue) {
      dataMap['interestDue'] = Number(rowData.interestDue)
    }

    const transaction: BulkSecurityTransferTransactionDerived = {
      rowId: Number(rowData.rowId),
      legacyOrigTranId: rowData.legacyOrigTranId,
      transactionId: rowData.transactionId,
      legacyTranId: rowData.legacyTranId,
      accountId: accountId,
      tranType: transTypeCode,
      units: rowData.units !== '' ? Number(rowData.units) : undefined,
      entryDate: rowData.entryDate !== '' ? rowData.entryDate : '',
      cusip: rowData.assetId,
      securityId: rowData.securityId,
      factor: rowData.factor,
      transferPrice:
        rowData.transferPrice !== ''
          ? Number(rowData.transferPrice)
          : undefined,

      inventoryType:
        Object.keys(InventoryType)[
          Object.values(InventoryType).indexOf(
            rowData.inventoryType as InventoryType,
          )
        ],
      dataMap: dataMap,
      settleDate: originalSettleDate,
      postDate: originalSettleDate,
    }

    const tpiDataMap: { [key: string]: TransferPurchaseInfo } = {}
    const basisTypeValues = this.basisTypeValues
    basisTypeValues.sort((a: Basis, b: Basis) => a.basisId - b.basisId)
    basisTypeValues.forEach((basisCurrent: Basis) => {
      const tpiRowMap: TransferPurchaseInfo = this.createTpiForBasisAndRow(
        basisCurrent.prefix,
        rowData,
      )
      if (tpiRowMap && this.tpiHasSomeData(tpiRowMap)) {
        this.plugEssentialInvisibleData(
          tpiRowMap,
          rowData,
          basisCurrent.basisId,
          false,
        )
        tpiDataMap[basisCurrent.basisId] = tpiRowMap
      }
    })

    return { transaction, tpiDataMap }
  }

  tpiHasSomeData(tpiRowMap: TransferPurchaseInfo): boolean {
    let tpiProp: keyof typeof tpiRowMap
    for (tpiProp in tpiRowMap) {
      if (tpiProp != 'basisId' && tpiProp != 'id') {
        if (
          tpiRowMap[tpiProp] &&
          tpiRowMap[tpiProp] !== null &&
          tpiRowMap[tpiProp] !== ''
        ) {
          return true
        }
      }
    }
    return false
  }

  async validate() {
    this.isDataValid = true
    this.isCommonValidationError = false
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransaction(bulkValidateView, TranCategoryUrl.TRANSFER)
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isDataValid = false
          this.isCommonValidationError = true
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isCommonValidationError = true
          this.isDataValid = false
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return params.value
    }
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }
  deduceTranTypeCellStyle(params: CellClassParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return { 'background-color': 'lightcoral' }
    } else {
      return this.getCellStyle(params)
    }
  }

  deduceTranTypeToolTip(params: ITooltipParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return 'Valid Value Required'
    } else {
      return this.toolTipValueGetter(params)
    }
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it

    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if erros ) and warnings (if onlky warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all erros in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()

      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        let validationMessage = validationInfo.message
        let basisPrefix: string | undefined = undefined
        const basisMatch = validationMessage.match(
          /For BasisId : (\d+) : (.*$)/,
        )

        if (basisMatch) {
          basisPrefix = this.basisTypeValues.find(
            basis => basis.basisId == parseInt(basisMatch[1]),
          )?.prefix

          validationMessage = basisMatch[2]
        }

        if (validationInfo.level == ErrorSeverity.ERROR) {
          validationInfo.impactedFields.forEach((impactedField: string) => {
            const deducedImpactedField = this.deduceImpactedField(
              impactedField,
              basisPrefix,
            )
            const currentKey = `${index}-${deducedImpactedField}`
            if (this.cellLevelErrorMessageMap.has(currentKey)) {
              this.cellLevelErrorMessageMap
                .get(currentKey)
                ?.push(validationMessage)
              errorMessages
                .get(this.deduceImpactedField(impactedField, basisPrefix))
                ?.push(validationMessage)
            } else {
              this.cellLevelErrorMessageMap.set(currentKey, [validationMessage])
              errorMessages.set(
                this.deduceImpactedField(impactedField, basisPrefix),
                [validationMessage],
              )
            }
          })
        } else {
          validationInfo.impactedFields.forEach((impactedField: string) => {
            const deducedImpactedField = this.deduceImpactedField(
              impactedField,
              basisPrefix,
            )
            const currentKey = `${index}-${deducedImpactedField}`
            if (this.cellLevelWarningMessageMap.has(currentKey)) {
              this.cellLevelWarningMessageMap
                .get(currentKey)
                ?.push(validationMessage)
              warningMessages
                .get(this.deduceImpactedField(impactedField, basisPrefix))
                ?.push(validationMessage)
            } else {
              this.cellLevelWarningMessageMap.set(currentKey, [
                validationMessage,
              ])
              warningMessages.set(
                this.deduceImpactedField(impactedField, basisPrefix),
                [validationMessage],
              )
            }
          })
        }
      })
      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  deduceImpactedField(
    impactedFieldFromValidationInfo: string,
    basisPrefix?: string,
  ): string {
    if (basisPrefix) {
      return this.concatToCamelCase(
        basisPrefix,
        impactedFieldFromValidationInfo,
      )
    }
    if (impactedFieldFromValidationInfo == 'securityId') {
      return 'assetId'
    }
    if (impactedFieldFromValidationInfo == 'orgUnits') {
      return 'units'
    }
    if (impactedFieldFromValidationInfo == 'accountId') {
      return 'account'
    } else {
      return impactedFieldFromValidationInfo
    }
  }

  createBulkValidationView(): BulkSecurityTransferValidationView {
    const view: BulkSecurityTransferValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: bulkRequestMode(this.transInput?.modalMode),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      tranCategory: TranCategory.TRANSFER,
      isTransferInDialog: true,
      basisDerivedField: this.basisDerivedField,
    }
    if (this.note.value !== '') view.transactionNote = this.note.value
    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }
  prepareBulkTransactionData(
    isValidate: boolean,
    rowData: BulkSecurityTransferRow,
  ): TransactionRequestWrapper {
    let currentOriginalTransaction = undefined
    if (!isBulkCopyModal(this.transInput)) {
      currentOriginalTransaction = this.originalTransactions.find(
        origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
      )
    }
    let createdDate = new Date().toISOString()
    if (currentOriginalTransaction?.createdDate) {
      createdDate = new Date(
        currentOriginalTransaction?.createdDate,
      ).toISOString()
    }
    const transTypeCode = Object.keys(TransactionTypeEnum).filter(
      x =>
        TransactionTypeEnum[x as TransactionTypeKey] ==
        getTransactionTypeNameFromCode(rowData.tranType),
    )[0] as TransactionTypeKey

    const originalSettleDate = rowData[
      'GAAPOrigSettleDate' as keyof typeof rowData
    ] as string

    let securityId = rowData.securityId
    if (!isBulkEditModal(this.transInput)) {
      securityId = this.cusipToSecurityMap.get(rowData.assetId)
    }

    const transactionData: BulkSecurityTransferTransaction = {
      tranType: transTypeCode,
      userId: this.userId ?? 0,
      accountId: this.resolveRowAccountId(rowData),
      entryDate: rowData.entryDate,
      securityId: securityId,
      postDate: originalSettleDate,
      settleDate: originalSettleDate,
      counterParty: getDefaultUnknownCounterParty(),
      counterPartyId: getDefaultUnknownCounterParty().id,
      // PVAL-26104: the proceeds the current derive cycle produced wins; the row's
      // own amount is the fallback. `??` and never `||`, so a server-derived 0 is
      // forwarded as 0 instead of falling through — a truthy check here is exactly
      // the shape of the bug this ticket is about. When neither slot is populated
      // the command carries no amount, identical to the pre-split behaviour.
      amount: rowData.derivedAmount ?? rowData.amount,
      typeLabel: rowData.transactionTypeLabel,
    }

    transactionData.dataMap = {}

    if (rowData.collateralBroker) {
      transactionData.dataMap.collateralBroker = rowData.collateralBroker
    }
    if (rowData.prepaidInterest) {
      transactionData.dataMap.prepaidInterest = Number(rowData.prepaidInterest)
    }
    if (rowData.interestDue) {
      transactionData.dataMap.interestDue = Number(rowData.interestDue)
    }
    if (
      rowData.transactionId &&
      this.initialClientLotIdMap.get(rowData.transactionId)
    ) {
      transactionData.dataMap.clientLotId = this.initialClientLotIdMap.get(
        rowData.transactionId,
      )
    }

    if (rowData.basisId) {
      transactionData.basisId = rowData.basisId
    }

    if (rowData.transferPrice !== '') {
      transactionData.transferPrice = Number(rowData.transferPrice)
    }
    if (rowData.units !== '') {
      if (isValidate) {
        transactionData.currUnits = Number(rowData.units)
      } else {
        transactionData.curUnits = Number(rowData.units)
      }

      transactionData.units = Number(rowData.units)
    }

    if (rowData.inventoryType !== '') {
      transactionData.inventoryType =
        Object.keys(InventoryType)[
          Object.values(InventoryType).indexOf(
            rowData.inventoryType as InventoryType,
          )
        ]
    } else {
      transactionData.inventoryType = 'LONG'
    }

    if (
      (this.sessionReconDate &&
        !isAfter(
          new Date(originalSettleDate),
          new Date(this.sessionReconDate),
        )) ||
      (!this.sessionReconDate &&
        this.selectedReconDate &&
        !isAfter(
          new Date(originalSettleDate),
          new Date(this.selectedReconDate),
        ))
    ) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    if (!isValidate) {
      transactionData.revisionId = currentOriginalTransaction?.revisionId
        ? currentOriginalTransaction.revisionId + 1
        : 1
      transactionData.sourceId = TransactionConstants.HELIOS_SOURCE_ID
      transactionData.createdDate = createdDate
      transactionData.modifiedDate = new Date().toISOString()
      transactionData.active = true
    }

    if (currentOriginalTransaction) {
      transactionData.transactionId = currentOriginalTransaction.transactionId
      transactionData.legacyOrigTranId =
        currentOriginalTransaction.legacyOrigTranId
      transactionData.legacyTranId = currentOriginalTransaction.legacyTranId
      transactionData.clearedDate = currentOriginalTransaction?.clearedDate
    }

    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      cusip: rowData.assetId,
      reprocessDownstreamTransfers: this.reprocessTransfers.value,
      basis: rowData.transactionBasis,
    }

    if (isBulkExternalModal(this.transInput)) {
      metaData.externalAccountId = rowData.externalAccountId
      metaData.executionId = rowData.executionId
      metaData.externalTranId = rowData.externalTranId
      metaData.externalCusip = rowData.externalCusip
      metaData.externalTranStatus = rowData.externalTranStatus
      metaData.dataSourceType = rowData.externalDataSourceType
    }
    const tpis = this.getRowTpis(rowData, true)
    if (tpis.length > 0) {
      metaData.tpis = tpis
    }
    if (rowData.mvtBases) {
      metaData.mvtBases = rowData.mvtBases
    }

    return { transactionData, metaData }
  }

  getRowTpis(
    rowData: BulkSecurityTransferRow,
    fromSave: boolean,
  ): TransferPurchaseInfo[] {
    const tpis: TransferPurchaseInfo[] = []
    this.basisTypeValues.forEach((basis: Basis) => {
      const tpiRowMap: TransferPurchaseInfo = this.createTpiForBasisAndRow(
        basis.prefix,
        rowData,
      )
      if (tpiRowMap && this.tpiHasSomeData(tpiRowMap)) {
        this.plugEssentialInvisibleData(
          tpiRowMap,
          rowData,
          basis.basisId,
          fromSave,
        )
        tpis.push(tpiRowMap)
      }
    })
    return tpis
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: (BulkSecurityTransferRow & unknown)[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })
    this.gridApi?.api.setRowData(newRowData)

    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.TRANSFER,
      requestMode: bulkRequestMode(this.transInput?.modalMode),
    }
    if (this.note.value !== '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }

    if (isBulkEditModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_SECURITY_TRANSFER_SAVE_NOTE_CW_EDIT
      this.bulkTransactionService
        .editBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else if (isBulkExternalModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_SECURITY_TRANSFER_SAVE_NOTE_EXTERNAL
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk External Transaction command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_SECURITY_TRANSFER_SAVE_NOTE_CW_CREATE
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction create command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    }
  }
  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }

  async loadTransactionDetailBulk(cwTransId: string[], securityId: number[]) {
    const transactionDetail$: Observable<ClearwaterTransactionDetailResponse> =
      this.bulkTransactionService.loadTransactionDetailBulk(
        cwTransId,
        securityId,
        TranCategoryUrl.TRANSFER,
        true,
      )
    return Promise.all([transactionDetail$.toPromise()]).then(
      ([transactionDetail]) => {
        if (this.transInput) {
          const unorderedTransactionDetails =
            transactionDetail.transactionDataMap
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const orderedTransactionDetails: { [key: string]: any } = {}

          // Sort the transaction details based on the order of cwTransId
          for (const id of cwTransId) {
            if (unorderedTransactionDetails[id]) {
              orderedTransactionDetails[id] = unorderedTransactionDetails[id]
            }
          }

          this.clearwaterTransactionDetail = orderedTransactionDetails
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(transactionDetail.securityMasterDataMap ?? ''),
          )
          this.tpiMapByTransaction = transactionDetail.tpiDataMap ?? {}
          this.mvtMapByTransaction = transactionDetail.mvtDataMap ?? {}
        }
      },
    )
  }

  async loadAdditionalDetailBulk(
    legacyOrigIds: number[],
    legacyTranIds: number[],
    securityIds: number[],
    accountIds: number[],
  ) {
    const additionalDetails$: Observable<AdditionalDetailsSecurityTransfer> =
      this.bulkTransactionService.loadAdditionalDetailsTransferBulk(
        legacyOrigIds,
        legacyTranIds,
        securityIds,
        accountIds,
        true,
      )
    return Promise.all([additionalDetails$.toPromise()]).then(
      ([additionalDetails]) => {
        if (this.transInput) {
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(additionalDetails.securityMasterDataMap ?? ''),
          )
          this.clientLotIdMapBulkPanel = new Map<string, string>(
            Object.entries(additionalDetails.clientLotIdMap ?? ''),
          )
          this.tpiMapByTransaction = additionalDetails.tpiDataMap ?? {}
          this.mvtMapByTransaction = additionalDetails.mvtDataMap ?? {}
        }
      },
    )
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  onBasisSelectionChange() {
    const basisColumnGroupsShown = this.selectedBasisPrefix.value as string[]
    const basisColumnGroupsHidden = this.basisTypeValues
      .filter(value => !this.selectedBasisPrefix.value.includes(value.prefix))
      .map((basis: Basis) => basis.prefix)
    basisColumnGroupsShown.forEach(basisPrefix => {
      const index = this.colDefs.findIndex(
        colDef => colDef.headerName === `${basisPrefix} Details`,
      )
      if (index == -1) {
        const applicableAccounts = this.findKeysWithBasisPrefix(basisPrefix)
        const basisInitialIndex =
          this.initialBasisIndexMap.get(basisPrefix) ?? 0
        let preMissingCount = 0
        for (let i = 0; i < basisInitialIndex; i++) {
          if (this.removedBasisIndex.has(i)) preMissingCount++
        }
        const basisIndex = basisInitialIndex - preMissingCount + 4
        this.colDefs.splice(
          basisIndex,
          0,
          this.addBasisColumn(
            basisPrefix,
            applicableAccounts.join().split(','),
          ),
        )
        this.removedBasisIndex.delete(basisInitialIndex)
      }
    })
    basisColumnGroupsHidden.forEach(basisPrefix => {
      const index = this.colDefs.findIndex(
        colDef => colDef.headerName === `${basisPrefix} Details`,
      )
      if (index > -1) this.colDefs.splice(index, 1)
    })
    const groupState = this.gridApi?.columnApi.getColumnGroupState()
    this.gridApi?.api.setColumnDefs(this.colDefs)
    if (groupState) this.gridApi?.columnApi.setColumnGroupState(groupState)

    this.selectedBasisMap = this.basisTypeValues.filter(value =>
      this.selectedBasisPrefix.value.includes(value.prefix),
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAllForDropdownItems(items: any[]) {
    items.forEach(element => {
      element['selectedAllGroup'] = 'selectedAllGroup'
    })
  }

  findKeysWithBasisPrefix(targetBasisPrefix: string): number[] {
    return Object.keys(this.basisMap)
      .map(key => Number(key))
      .filter(key =>
        this.basisMap[key].some(basis => basis.prefix === targetBasisPrefix),
      )
  }

  expandCollapseAllBasis(expandAll: boolean) {
    this.selectedBasisPrefix.value.forEach((basisPrefixSelected: string) => {
      const group =
        this.gridApi?.columnApi.getOriginalColumnGroup(basisPrefixSelected)
      if (group) this.gridApi?.columnApi.setColumnGroupOpened(group, expandAll)
    })
  }

  concatToCamelCase<T extends string, U extends string>(x: T, y: U): string {
    const camelCaseY = y.charAt(0).toUpperCase() + y.slice(1)
    return `${x}${camelCaseY}`
  }

  returnDisIntegratedTpiProperty(
    basisPrefix: string,
    row: BulkSecurityTransferRow,
    property: string,
  ):
    | string
    | number
    | Map<string, string[]>
    | { [k: string]: Basis }
    | null
    | MvtBasisSelection[]
    | SleeveSimple[]
    | TransactionTypeLabel
    | undefined {
    const rowField = this.concatToCamelCase(
      property == 'taxDaysHeld' ? '' : basisPrefix,
      property,
    ) as keyof typeof row
    return row[rowField]
  }

  createTpiForBasisAndRow(
    basisPrefix: string,
    rowData: BulkSecurityTransferRow,
  ) {
    const tpi: TransferPurchaseInfo = {} as TransferPurchaseInfo
    TpiPropertiesNumeric.forEach(keyNumeric => {
      ;(tpi[keyNumeric as keyof typeof tpi] as number | undefined) =
        this.returnDisIntegratedTpiProperty(
          basisPrefix,
          rowData,
          keyNumeric,
        ) !== ''
          ? Number(
              this.returnDisIntegratedTpiProperty(
                basisPrefix,
                rowData,
                keyNumeric,
              ),
            )
          : undefined
    })
    TpiPropertiesString.forEach(keyString => {
      ;(tpi[keyString as keyof typeof tpi] as string | undefined) =
        this.returnDisIntegratedTpiProperty(
          basisPrefix,
          rowData,
          keyString,
        )?.toString()
    })

    return tpi
  }

  plugEssentialInvisibleData(
    tpiRowMap: TransferPurchaseInfo,
    rowData: BulkSecurityTransferRow,
    basisId: number,
    fromSave: boolean,
  ) {
    if (!tpiRowMap.basisId) tpiRowMap.basisId = basisId
    if (!tpiRowMap.id && rowData.legacyTranId)
      tpiRowMap.id = rowData.legacyTranId

    if (!tpiRowMap.factor && rowData.factor) tpiRowMap.factor = rowData.factor

    if (!tpiRowMap.origYield) {
      tpiRowMap.origYield = 0.0
      tpiRowMap.amortStartYield = 0.0
    }
    if (tpiRowMap.amortStartDate) {
      tpiRowMap.valuationDate = tpiRowMap.amortStartDate
    }
    if (!tpiRowMap.amortStartDate) {
      tpiRowMap.amortStartDate = tpiRowMap.origSettleDate
      tpiRowMap.valuationDate = tpiRowMap.origSettleDate
    }

    if (!tpiRowMap.amortOverride) {
      tpiRowMap.amortOverride = 'OBEY_ACCOUNT_SECURITY'
    }

    if (fromSave && !tpiRowMap.amortStartPrice && tpiRowMap.origPrice) {
      tpiRowMap.amortStartPrice = tpiRowMap.origPrice
    }
  }

  adjustTransactionsPostDelete() {
    this.deletePostProcessingInProgress = true
    this.missingTranRowList.clear()
    this.disableValidate = true
    this.gridApi?.api.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        if (rowNode.data.tranType == '') {
          this.missingTranRowList.add(rowNode.data.rowId.toString())
        }
      }
    })
    if (this.missingTranRowList.size == 0) this.disableValidate = false
    this.deletePostProcessingInProgress = false
  }

  async loadNonSessionAccountTypeInfo(
    uniqueAccountId: number[],
  ): Promise<void> {
    const accountInfoPromise = this.bulkTransactionService
      .fetchAccountTypes(uniqueAccountId)
      .toPromise()
    await Promise.all([accountInfoPromise]).then(async accountTypeObj => {
      if (!accountTypeObj) return
      this.sleeveSimpleAccount = Object.entries(accountTypeObj[0])
        .map(([key, value]) => {
          return {
            id: Number(key),
            type: value,
          }
        })
        .filter(data => data.type === 'sleeveAggregate')

      if (this.sleeveSimpleAccount.length > 0) {
        this.sleeveAccountIds = this.sleeveSimpleAccount.map(({ id }) => id)

        const subAccounts = await this.bulkTransactionService
          .fetchAllSubAccounts(this.sleeveAccountIds)
          .toPromise()

        if (!subAccounts) return

        this.bulkSleeveSimpleAccount = Object.entries(subAccounts).map(
          ([key, value]) => {
            return {
              id: Number(key),
              sleeveSimple: value,
            }
          },
        )
      }
    })
  }

  isAccountEditable(params: EditableCallbackParams): boolean {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType ||
      this.transInput?.externalTransaction
    ) {
      if ('sleeveAggregate' === this.sessionAccountInfo?.accountType) {
        return this.isSessionAccountEditable()
      } else {
        return this.isNonSessionAccountsEditable(params)
      }
    } else if (!this.isEdit) {
      return true
    }
    return false
  }

  isSessionAccountEditable() {
    return true
  }

  isNonSessionAccountsEditable(params: EditableCallbackParams) {
    if (this.sleeveAccountIds?.includes(params.data['externalAccountId'])) {
      return true
    }
    return false
  }

  async setSessionAccounInfo(accountInfo?: AccountInfo) {
    if (accountInfo) {
      this.sessionAccountInfo = accountInfo
      if ('sleeveAggregate' === accountInfo.accountType) {
        const subAccounts = await this.bulkTransactionService
          .fetchSubAccounts(accountInfo.accountId)
          .toPromise()
        this.sleeveSimpleAccount = subAccounts
      }
    }
  }

  hideAccountForSleeveAggregate(accountId: number) {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType &&
      this.sessionAccountInfo?.accountId == accountId
    ) {
      return true
    } else if (this.sleeveAccountIds?.includes(accountId)) {
      return true
    }
    return false
  }
  clearAll() {
    this.gridApi?.api.applyTransaction({
      remove: this.gridApi?.api.getRenderedNodes().map(rowNode => rowNode.data),
    })

    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.rowData })
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    this.disableValidate = true
    this.errorDetails = ''
  }

  onValidityStatusChanged(event: CellValueChangedEvent) {
    if (
      event.node.data.tranType == '' &&
      event.node.data.validityStatus == 'invalid'
    ) {
      this.missingTranRowList.add(event.node.data.rowId)
      this.disableValidate = true
    }
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0) ||
      (params?.data.tranType == '' && params?.data.validityStatus != 'inactive')
    ) {
      return true
    }
    return false
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }
}
